import { z } from 'zod';
/**
 * Telemetry pipeline health, for platform administrators.
 *
 * This answers one question — *is CAN telemetry flowing, and will it keep
 * flowing* — for the whole platform rather than for a tenant. It is deliberately
 * cross-tenant and therefore lives behind `@PlatformAuthorized()`, alongside
 * tenant provisioning and the platform audit trail.
 *
 * It reports two different kinds of thing, and the difference is the whole
 * design:
 *
 *  - **Outcomes**, derived from the database as it stands — partition headroom,
 *    rollup lag, queue depth. These answer *are we broken*. They are the
 *    stronger signal, because if partitions extend a week ahead it does not
 *    matter whether today's run happened.
 *  - **Recorded job runs** (`jobs`), answering *are we about to be*. Outcomes
 *    alone cannot: both telemetry jobs catch, log and continue, so partition
 *    maintenance can fail every morning for a week while every derived figure
 *    stays green, and then ingestion stops outright with no warning at all.
 *
 * Neither subsumes the other. A job can succeed while its outcome is bad — a
 * rollup that runs perfectly cannot recover hours whose raw samples were
 * already dropped — and an outcome can be fine while the job that sustains it
 * is dead. Both are reported.
 *
 * No figure here is about a vehicle. Nothing on this page is tenant-facing.
 */
/**
 * Days of partition headroom below which the platform is in trouble.
 *
 * A partitioned table with no matching partition REJECTS every insert, so this
 * is not a degradation curve: ingestion works, and then it stops. `CRITICAL` is
 * set at one day because tomorrow's samples are the first to be refused, and
 * `WARNING` at three to leave a working day's notice.
 *
 * These are thresholds against the *configured* lookahead
 * (`TELEMETRY_PARTITION_LOOKAHEAD_DAYS`, default 7) rather than absolutes, so a
 * deployment that runs a wider window is not permanently amber.
 */
export const PIPELINE_PARTITION_CRITICAL_DAYS = 1;
export const PIPELINE_PARTITION_WARNING_DAYS = 3;
/**
 * Rollup backlog thresholds, in hours awaiting summarisation.
 *
 * `WARNING` matches the worker's own `hours.length > 24` log line so the page
 * and the logs do not disagree about what "behind" means. There is no fixed
 * critical threshold: the real one is raw retention, because an hour whose raw
 * samples have been dropped can never be summarised. That is reported
 * separately as `hoursBeyondRecovery`, which is CRITICAL at any value above
 * zero.
 */
export const PIPELINE_ROLLUP_WARNING_HOURS = 24;
/**
 * How many hours of backlog the API will count before giving up and saying so.
 *
 * The backlog query scans raw telemetry, so it is bounded. When the cap is hit
 * the response sets `backlogTruncated`, and the page says "at least N" rather
 * than presenting a cap as a measurement.
 */
export const PIPELINE_ROLLUP_BACKLOG_CAP = 720;
/** Tenants listed in the ingestion breakdown, busiest first. */
export const PIPELINE_TENANT_BREAKDOWN_LIMIT = 10;
/**
 * Distinct failure messages listed for failed events, most common first.
 *
 * Bounded because the messages are grouped verbatim: an error embedding an id
 * produces one group per occurrence, and an unbounded list would be the failed
 * events themselves rather than a summary of them. The response reports how
 * many distinct messages exist so the truncation is visible.
 */
export const PIPELINE_FAILED_REASON_LIMIT = 8;
/** Hours in the ingestion series. One day, to match the headline counts. */
export const PIPELINE_INGESTION_SERIES_HOURS = 24;
/**
 * Runs shown in each job's history strip.
 *
 * Twenty because it has to cover a meaningful stretch of the *fastest* job:
 * the rollup runs every fifteen minutes, so this is five hours of it. The
 * strip's purpose is to separate "started failing an hour ago" from "has
 * failed every third run all week", which a single verdict cannot express.
 */
export const PIPELINE_JOB_HISTORY_LENGTH = 20;
export const pipelineStatusSchema = z.enum(['HEALTHY', 'WARNING', 'CRITICAL']);
/**
 * The scheduled worker jobs whose runs are recorded, and what a failure costs.
 *
 * Shared so the worker, the API and the page all name a job the same way — the
 * `job` column is a bare string, and three independent spellings of
 * "telemetry-hourly-rollup" would silently produce three jobs that each look
 * like they never ran.
 *
 * `severityOnFailure` differs per job on purpose. A failing partition job
 * becomes an outage on a known clock and is worth a red badge before anything
 * has actually broken — that early warning is the entire reason these rows
 * exist. A failing attribution sweep costs attribution quality and nothing
 * else, and treating the two alike would make the red badge meaningless.
 *
 * `everyMinutes` is what the worker's own `setInterval` uses; a job is overdue
 * at twice that, which tolerates one missed cycle without crying wolf.
 */
export const PIPELINE_JOBS = {
    'scheduled-reports': {
        label: 'Scheduled reports',
        everyMinutes: 5,
        severityOnFailure: 'WARNING',
        consequence: 'Scheduled reports stop being generated and delivered. Nobody is told: the whole value of a report is that it arrives unprompted, so its ABSENCE is the symptom, and the person expecting it is the last to know.',
        remedy: 'Check the worker log for “scheduled report generation failed”, then the run history for the affected schedule — a failure records the provider’s own error. A run that fails four times is abandoned until its next occurrence rather than retried forever, so a fixed problem resolves itself on the next cycle.',
    },
    'daily-headlines': {
        label: 'Daily headlines',
        everyMinutes: 60,
        /*
         * ⚠️ WARNING, not CRITICAL, and the difference is consequence rather than
         * importance. A missed run costs one day of a history page; every fact
         * behind it — incidents, recommendations, the audit trail — outlives the
         * headline set and is still readable. Nothing stops arriving and no
         * decision is made on worse information.
         *
         * The cost that IS real: a day missed cannot be recovered later, because a
         * day reconstructed from a backlog that has since been worked is not what
         * was judged at the time. That is why it runs hourly rather than at
         * midnight — a restart during the small hours would otherwise skip a day
         * permanently.
         */
        severityOnFailure: 'WARNING',
        consequence: 'The day’s news stops being recorded. Today still reads live, so nothing looks broken — the loss only shows up later as a gap in the history, and a day that was never recorded cannot be filled in afterwards: reconstructing it from a backlog that has since been worked would present today’s state as that day’s news.',
        remedy: 'Check the worker log for “recording daily headlines failed”. A failure for one tenant is logged and skipped rather than abandoning the run, so the message names which. The write is conditional on absence, so re-running is safe and changes nothing that was already recorded.',
    },
    'provider-poll': {
        label: 'Provider polling',
        everyMinutes: 1,
        /*
         * CRITICAL, unlike the geofence sync beside it, and the difference is a
         * claim about consequence rather than importance.
         *
         * A stalled geofence sync leaves incidents being raised exactly as before —
         * only a boundary goes stale. A stalled POLL means a provider that cannot
         * push has no other way in: events stop arriving entirely, and the symptom
         * is an absence, which is the hardest thing for a customer to notice. That
         * puts it beside partition maintenance rather than beside enrichment.
         */
        severityOnFailure: 'CRITICAL',
        consequence: 'Providers that cannot push stop delivering. Events and telemetry simply stop arriving for those tenants, with no error anywhere a customer can see — the symptom is an absence, and the fleet looks quiet rather than broken.',
        remedy: 'Check the affected Provider Connection: a poll failure records the provider’s own error against it, and “attempted” and “succeeded” are separate so an intermittent failure cannot hide behind a recent timestamp. A connection whose provider is down backs off to its own interval rather than retrying every run.',
    },
    'trip-close': {
        label: 'Close abandoned trips',
        everyMinutes: 5,
        /*
         * CRITICAL, and it is the data loss rather than the job that earns it.
         *
         * A vehicle that stops reporting mid-journey produces no further sample, so
         * INGESTION can never close its trip — nothing else ever will. Meanwhile raw
         * telemetry is dropped at TELEMETRY_RETENTION_DAYS and position has no
         * hourly rollup, so once the samples are gone the trip cannot be
         * reconstructed at any price.
         *
         * The failure is also silent in the worst way: trips keep being derived for
         * every vehicle still reporting, so the feature LOOKS healthy while a
         * growing tail of journeys stays permanently open — reading as vehicles
         * still driving, days later.
         */
        severityOnFailure: 'CRITICAL',
        consequence: 'Trips for vehicles that stopped reporting stay open forever. They read as journeys still in progress, their duration and distance are never finalised, and once the raw telemetry passes its retention window the trip can never be completed — the evidence is gone.',
        remedy: 'The job closes a trip at its LAST SAMPLE, never at the time it ran, so a late run loses accuracy in nothing but timeliness — running it after a fix repairs the backlog. Check the worker is up and that TripRepository.findAbandoned is returning; the trips it is waiting on are those with no endedAt and a lastSampleAt older than the gap tolerance.',
    },
    'provider-geofence-sync': {
        label: 'Geofence sync',
        everyMinutes: 60,
        /*
         * WARNING rather than CRITICAL, and that is a claim about the design rather
         * than about the job's importance. Zones are auto-provisioned from the
         * events that name them, so a stalled sync leaves incidents being raised
         * exactly as before — only the boundary and the upstream classification go
         * stale. Calling that critical would put this beside partition maintenance,
         * where the outage is real and arrives on a clock.
         */
        severityOnFailure: 'WARNING',
        consequence: 'Zone boundaries and provider classifications stop being refreshed. Nothing stops working: a zone named by an event is still created automatically and its exits still raise incidents, so the effect is a map that drifts out of date and new zones sitting unclassified for longer.',
        remedy: 'Check the connection on Provider Connections — the last attempt records the provider’s own error beside the last success. A connection with no base URL or no outbound credential is not configured for this and is skipped rather than failed.',
    },
    'log-store-maintenance': {
        label: 'Log store maintenance',
        everyMinutes: 24 * 60,
        /*
         * ⚠️ WARNING, not CRITICAL, and the difference from telemetry's partition
         * job — which looks identical — is what the failure COSTS.
         *
         * When telemetry runs out of partitions, ingestion stops and a customer's
         * fleet goes dark. When this stops, the platform is unaffected: every line
         * still reaches stdout, because the writer tees there BEFORE the store is
         * considered, and the writer additionally self-heals a missing partition
         * once per minute. What is lost is searchability — bad, and not an outage.
         */
        severityOnFailure: 'WARNING',
        consequence: 'Creates the log store’s partitions ahead of time and drops those past their tier’s retention. While this fails, logging continues to stdout and the writer will create a missing partition itself — but expired partitions are never dropped, so the log database grows without bound.',
        remedy: 'Check the worker log for the code LOG_STORE_MAINTENANCE_FAILED. If OIP_LOG_DATABASE_URL is unset the job reports that it had nothing to do, which is a healthy run rather than a fault — "not configured" and "broken" are deliberately different.',
    },
    'log-summary': {
        label: 'Log summary rollup',
        /*
         * ⚠️ FIVE MINUTES because §6 says so — "summary tables refreshed by scheduled
         * jobs at least every 5 minutes" — and because the bucket is five minutes:
         * a slower run would leave the newest bucket permanently partial, so the
         * figure a reader watches during an incident would be the least trustworthy
         * one on the page.
         */
        everyMinutes: 5,
        /*
         * ⚠️ WARNING rather than CRITICAL, for the reason the maintenance job beside
         * it is: while this fails nothing stops being LOGGED and nothing stops being
         * SEARCHABLE — only the dashboard goes stale, and it says how stale by
         * reporting the watermark rather than drawing a confident empty chart.
         */
        severityOnFailure: 'WARNING',
        consequence: 'Rolls raw log lines into the five-minute summary tables the operational dashboard reads. While this fails the dashboard freezes at its last refresh: search, alerting on live conditions and stdout are unaffected, but "errors in the last hour" silently stops moving.',
        remedy: 'Check the worker log for the code LOG_SUMMARY_FAILED. If OIP_LOG_DATABASE_URL is unset the job reports that it had nothing to do, which is a healthy run rather than a fault. A run reporting `truncated` has fallen far enough behind that the oldest buckets could not be recovered — raw lines past their tier retention are gone, so that period stays empty.',
    },
    'platform-alerts': {
        label: 'Platform alert evaluation',
        /*
         * ⚠️ FIVE MINUTES, matching the summary rollup this partly reads — evaluating
         * faster would judge the same figures twice, and slower would mean a
         * condition can be live for longer than the summary is stale before anybody
         * is told.
         */
        everyMinutes: 5,
        /*
         * ⚠️ **CRITICAL, unlike the two log jobs beside it, and the difference is
         * what the failure COSTS.** When the rollup stops, a dashboard freezes and
         * says so. When THIS stops, nothing is judged at all — and the symptom is an
         * empty alert list, which is indistinguishable from a healthy platform. §7
         * requires the evaluator to be self-monitored for exactly that reason.
         */
        severityOnFailure: 'CRITICAL',
        consequence: 'Evaluates every §7 alert rule against its own data source and opens, groups or resolves the resulting alerts. While this fails nothing is judged: no new condition is raised and no cleared condition is resolved, so the alert list silently stops moving and reads as a quiet platform.',
        remedy: 'Check the worker log for PLATFORM_ALERT_EVALUATION_FAILED, which is the whole run, or PLATFORM_ALERT_SOURCE_FAILED, which is one source — a failing source leaves ITS rules’ alerts untouched rather than resolving them, deliberately, so the run is partial rather than wrong.',
    },
    'platform-alerts-writers': {
        label: 'Log writer health',
        /*
         * ⚠️ **TWO MINUTES, and the number comes from acceptance criterion 9** —
         * *"stopping the log writer … produces a notification within 5 minutes."*
         * Detection is the staleness threshold PLUS this interval, so two plus two
         * is four and the criterion is met with a minute to spare. Inside the
         * five-minute `platform-alerts` pass the worst case was ten.
         */
        everyMinutes: 2,
        /*
         * ⚠️ **CRITICAL, like the evaluation it was split out of.** It is the only
         * thing that notices the log writer has stopped, and a stopped writer means
         * lines are being lost with no error anywhere — the symptom is an absence.
         */
        severityOnFailure: 'CRITICAL',
        consequence: 'Evaluates the log writer heartbeat rules — silent writers, dropped ERROR or security lines, and ingestion lag — on their own faster schedule. While this fails, a writer that stops is noticed only by the five-minute pass, so the notification can take up to ten minutes instead of four.',
        remedy: 'Check the worker log for PLATFORM_ALERT_EVALUATION_FAILED. This pass reads only the heartbeat table in the transactional database, so a failure here is usually that database rather than the log store.',
    },
    'alert-evaluator-watchdog': {
        label: 'Alert evaluator watchdog',
        /*
         * ⚠️ **FIVE MINUTES to CHECK, thirty before it CONCLUDES anything.** The
         * threshold lives in `DEFAULT_WATCHDOG_THRESHOLDS`, not here: checking often
         * costs one indexed read and buys a prompt warning, while concluding quickly
         * would send the alert of last resort during an ordinary deploy.
         */
        everyMinutes: 5,
        /*
         * ⚠️ **WARNING, not CRITICAL, and it is the one job where that is not about
         * consequence.** Its failure IS serious — nothing then watches the
         * evaluator. But escalating it puts a red badge on the pipeline health page
         * whenever this job itself struggles, on a page a reader consults to judge
         * everything ELSE, and the condition it watches for is already reported by
         * `/platform/alerts` going stale. The honest answer is that nothing can
         * fully monitor itself from inside; the external uptime check is what covers
         * the whole worker being gone.
         */
        severityOnFailure: 'WARNING',
        consequence: 'Watches the platform alert evaluator’s own heartbeat and emails platform administrators directly if it stops. While this fails nobody is told that alerting has stopped — the alerts screen still says so, but only to somebody who opens it.',
        remedy: 'Check the worker log for ALERT_WATCHDOG_FAILED. If the evaluator itself is the problem, platform-alerts is the job to look at; this one only reports it.',
    },
    'telemetry-partition-maintenance': {
        label: 'Partition maintenance',
        everyMinutes: 24 * 60,
        severityOnFailure: 'CRITICAL',
        consequence: 'Creates tomorrow’s partition and drops expired ones. While this fails nothing changes — until the lookahead window is exhausted, at which point every telemetry insert is rejected.',
        remedy: 'Check the worker log for “telemetry partition maintenance FAILED”. Usually a database permission or connection problem rather than a code fault. Partitions can be created by hand with create_vehicle_telemetry_partition(date) to buy time.',
    },
    'telemetry-hourly-rollup': {
        label: 'Hourly rollup',
        everyMinutes: 15,
        severityOnFailure: 'CRITICAL',
        consequence: 'Summarises raw telemetry into the hourly table that trends, CAN health and class comparison read. Hours missed past raw retention can never be rebuilt.',
        remedy: 'Check the worker log for “telemetry hourly rollup failed”. The job catches up by itself once it runs again, so the urgency is the raw retention boundary rather than the backlog size.',
    },
    'anomaly-daily': {
        label: 'Anomaly detection',
        everyMinutes: 6 * 60,
        /*
         * ⚠️ WARNING, not CRITICAL, and that is a claim about CONSEQUENCE rather
         * than importance. While this fails the platform behaves exactly as it did
         * before anomaly detection existed — every rule still fires, every incident
         * is still raised. What is lost is a day's derived features, and a feature
         * day can be recomputed for as long as the hourly rollup survives, which is
         * far longer than raw telemetry.
         */
        severityOnFailure: 'WARNING',
        consequence: 'Derives each vehicle’s daily measurements, refreshes baselines and judges them. Rule-derived incidents are unaffected. A missed day can be recomputed while the hourly rollup still holds it, so a short outage costs nothing permanent — a long one costs the baselines their most recent history.',
        remedy: 'Check the worker log for “anomaly daily pass failed”. On a tenant with no telemetry it correctly does nothing and records a run, so “ran, evaluated 0” is a fact about the fleet rather than a fault. Suppression is expected to be high on a young tenant: a baseline needs three weeks before it can judge anything.',
    },
    'anomaly-publish': {
        label: 'Anomaly publication',
        everyMinutes: 6 * 60,
        /*
         * ⚠️ WARNING, like the detection pass and for a stronger reason: while this
         * fails NOTHING is lost and nothing is wrong on any screen. Every anomaly
         * incident is already raised, scored and in the queue — the incident exists
         * whether or not it is published (§10.2). What stops is the RECORD of what
         * the day published, which the budget counts and the review-rate metric
         * reads.
         *
         * ⚠️ The cost of a long outage is subtler and worth knowing: with no records
         * accruing, the rolling allowance reads as unspent, so the first successful
         * run after it may publish a larger day than the budget intended.
         */
        severityOnFailure: 'WARNING',
        consequence: 'Applies the alert-volume budget to the day’s baseline-derived incidents and records what was published. Incidents are unaffected — they are raised, scored and queued by the detection pass regardless. What stops is the publication record, which the budget counts and the review-rate metric reads.',
        remedy: 'Check the worker log for “anomaly publication pass failed”. A tenant in shadow mode is skipped deliberately and records no runs of its own, so “ran, 0 published” is the expected result until a tenant is switched to LIVE. “anomaly budget held items back” is not a fault: it is the control working, and the count is what §19.0b measures.',
    },
    'alert-fanout': {
        label: 'Alert fan-out',
        everyMinutes: 1,
        severityOnFailure: 'CRITICAL',
        consequence: 'Turns raised alert events into per-person deliveries. While this fails the outbox still accumulates, so nothing is lost — but nobody is told anything, and a mandatory security notification is as delayed as a routine one.',
        remedy: 'Check the worker log for “alert fan-out failed”. The drain is idempotent and catches up on the next run, so a brief failure costs latency rather than messages. A persistent one means alerting is silently off.',
    },
    'alert-dispatch': {
        label: 'Alert dispatch',
        everyMinutes: 1,
        severityOnFailure: 'CRITICAL',
        consequence: 'Renders and sends queued alerts. While this fails the deliveries queue up and nothing is lost, but nobody receives anything — and a suppressed destination is not detected either, so failures keep being retried.',
        remedy: 'Check the worker log for “alert dispatch failed”, then the dead-letter view for permanent failures. A provider outage resolves itself once the provider returns; a configuration fault (no SMTP host, no bot token) shows as “no transport” on every row for that channel.',
    },
    'driver-attribution-sweep': {
        label: 'Driver attribution sweep',
        /*
         * ⚠️ **WAS 60, AND THE JOB HAS ALWAYS RUN DAILY** — so this page reported a
         * perfectly healthy sweep as OVERDUE for twenty-two hours out of every
         * twenty-four. `overdue` is `lastSuccess > everyMinutes × 2`, the timer is
         * `RETENTION_SWEEP_INTERVAL_MS`, and its default is 24 hours with a comment
         * beside it saying daily is ample for a 30-day clock. Nothing was wrong with
         * the job; the declaration was.
         *
         * Found by the drift guard in `apps/worker/src/schedule.test.ts`, which exists
         * because this constant and the worker's timers are two statements of one
         * fact. A permanent red badge is worse than none — it is precisely the failure
         * this page is built to avoid, and it teaches a reader to ignore the others.
         *
         * ⚠️ The interval is CONFIGURABLE, so a deployment that sweeps faster is
         * policed more loosely than it could be. That is the safe direction; the guard
         * permits a timer faster than the declaration and never slower.
         */
        everyMinutes: 24 * 60,
        severityOnFailure: 'WARNING',
        consequence: 'Attributes events to drivers after the fact. A failure costs attribution quality on recent events; nothing stops working and nothing is lost permanently.',
        remedy: 'Check the worker log for “driver attribution sweep failed”. Safe to leave until the next working day — the sweep is idempotent and picks up whatever it missed.',
    },
};
/**
 * A job is overdue at this multiple of its interval.
 *
 * Two rather than one so a single missed cycle — a slow run, a restart — is not
 * reported as a fault. The partition job's window is a day, so this is a day of
 * grace on a seven-day budget.
 */
export const PIPELINE_JOB_OVERDUE_FACTOR = 2;
/** Days of run history kept. Pruned by the daily maintenance job. */
export const PIPELINE_JOB_RETENTION_DAYS = 30;
/**
 * When a job that is currently succeeding is nonetheless unreliable.
 *
 * The last-run verdict alone calls a job healthy if its most recent attempt
 * worked, which is true and misleading: a rollup failing one run in three is
 * doing its work eventually, by luck of the retry cycle, and will stop doing so
 * the day the failure rate rises. The history strip made that visible; this
 * makes it a status.
 *
 * Two conditions, both required. The RATE stops a long clean history being
 * condemned by one old blip. The MINIMUM stops a short window over-reacting —
 * after a worker restart there may be only three runs, and one failure among
 * them is 33% without being evidence of anything.
 *
 * Intermittent is always WARNING and never the job's failure severity: the work
 * IS getting done, so it is "look into this", not "act now". Escalating it
 * would put a partition job that is succeeding into the same red state as one
 * that is not, and the difference between those matters more than either badge.
 */
export const PIPELINE_JOB_INTERMITTENT_RATE = 0.2;
export const PIPELINE_JOB_INTERMITTENT_MIN_FAILURES = 2;
/** Why a job has no status rather than a healthy one. */
export const pipelineJobUnavailableSchema = z.enum(['NEVER_RUN']);
/**
 * What the scheduled-job lease says about a job at this instant.
 *
 * ⚠️ **`holder` is EVIDENCE FOR A READER, never an authorization input.** It is a
 * hostname by default, two replicas may legitimately share one, and the claim is
 * won atomically regardless of what it says. Same rule as `AuditEvent.ipAddress`,
 * which comes from a header a direct caller can forge.
 */
export const leaseStateSchema = z.object({
    state: z.enum(['RUNNING', 'ABANDONED', 'IDLE', 'UNCLAIMED']),
    /** Which replica last claimed it. Null when no lease row exists. */
    holder: z.string().nullable(),
    /** When the claim was taken — how long a RUNNING job has been running. */
    claimedAt: z.string().datetime().nullable(),
    /** The earliest instant this job may run again, on ANY replica. */
    nextDueAt: z.string().datetime().nullable(),
});
export const pipelineJobSchema = z.object({
    job: z.string(),
    label: z.string(),
    status: pipelineStatusSchema,
    /** `NEVER_RUN` when no row exists at all — distinct from a job that failed. */
    unavailable: pipelineJobUnavailableSchema.nullable(),
    lastRunAt: z.string().datetime().nullable(),
    lastRunAgeMinutes: z.number().nullable(),
    /** Null while a run is in flight, so "running" is not reported as failure. */
    lastRunOk: z.boolean().nullable(),
    lastSuccessAt: z.string().datetime().nullable(),
    lastSuccessAgeMinutes: z.number().nullable(),
    /**
     * The most recent FAILED run in the window, whether or not the last run
     * failed.
     *
     * This replaced a bare `lastError` that was only ever populated when the
     * MOST RECENT run failed — so a job reported as "succeeding, but 3 of the
     * last 10 runs failed" carried no reason at all, which is the case where the
     * reason matters most: the job looks alive and the fault cannot be
     * reproduced on demand. Every failure's message was already in
     * `JobRun.error`; nothing read it.
     *
     * One field rather than two, because `lastError` and "the last failure"
     * agree exactly whenever both exist, and two fields that must never disagree
     * eventually will.
     *
     * `error` is nullable inside it: a run can fail without recording a message,
     * and "failed, no reason recorded" is a real finding rather than an absence.
     */
    lastFailure: z
        .object({
        at: z.string().datetime(),
        error: z.string().nullable(),
        /** True when this failure IS the most recent run, so the UI need not compare dates. */
        isMostRecentRun: z.boolean(),
    })
        .nullable(),
    /** Consecutive failures since the last success — one flake reads differently from a dead job. */
    consecutiveFailures: z.number().int(),
    /**
     * Succeeding now, but failing often enough in the recent window to be
     * unreliable. Only ever set when the last run SUCCEEDED — a job that is
     * currently failing is reported as failing, not as flaky.
     */
    intermittent: z.boolean(),
    /** Failed share of the completed runs in the window. Runs in flight are excluded. */
    failureRate: z.number(),
    failedRuns: z.number().int(),
    completedRuns: z.number().int(),
    itemsProcessed: z.number().int().nullable(),
    expectedEveryMinutes: z.number().int(),
    overdue: z.boolean(),
    consequence: z.string(),
    /** What to do when this job is failing. A status without a next step is half an answer. */
    remedy: z.string(),
    /**
     * Recent outcomes, OLDEST FIRST so the strip reads left to right like time.
     * `ok` is null for a run that was still in flight.
     */
    recentRuns: z.array(z.object({ at: z.string().datetime(), ok: z.boolean().nullable() })),
    /**
     * What the scheduled-job LEASE says about this job right now.
     *
     * ⚠️ **This resolves an ambiguity `JobRun` records about ITSELF.** Its
     * `finishedAt` is documented as "null while the run is in flight, OR if the
     * worker died mid-run" — two opposite facts that read identically, and the
     * second is invisible today: an abandoned run is never counted as a failure,
     * so a job whose run died can report HEALTHY. The lease can tell them apart,
     * because a claim carries a DEADLINE.
     *
     *   RUNNING   a replica holds the claim and its deadline has not passed
     *   ABANDONED a replica claimed and never released, and the deadline HAS
     *             passed — it died mid-run
     *   IDLE      no run in flight; released normally
     *   UNCLAIMED no lease row at all, so this job has never been claimed
     */
    lease: leaseStateSchema,
});
export const pipelineJobsSchema = z.object({
    status: pipelineStatusSchema,
    items: z.array(pipelineJobSchema),
    overdueFactor: z.number(),
    retentionDays: z.number().int(),
    historyLength: z.number().int(),
    intermittentAboveRate: z.number(),
    intermittentMinFailures: z.number().int(),
});
/**
 * Ingestion volume.
 *
 * Note the absence of a status. On a platform with no live provider connection
 * — every development machine, and this one during a demo — "no samples in the
 * last hour" is the normal state, and a permanently red badge would train
 * people to ignore the page. The figures are reported; the judgement is not
 * made, for the same reason CAN health refuses to call 90 °C hot.
 */
export const pipelineIngestionSchema = z.object({
    samplesLastHour: z.number().int(),
    samplesLast24h: z.number().int(),
    /** Distinct vehicles that sent anything in 24 hours. */
    vehiclesReporting: z.number().int(),
    /** Vehicles that could have: not archived. The gap is the interesting part. */
    vehiclesActive: z.number().int(),
    newestSampleAt: z.string().datetime().nullable(),
    newestSampleAgeMinutes: z.number().nullable(),
    /**
     * Sample counts per hour, oldest first, exactly `seriesHours` long.
     *
     * Zero is a real measurement here — no rows in an hour means no samples
     * arrived — so the series is padded rather than sparse. That is deliberately
     * the opposite of the rule everywhere else, where an absent parameter must
     * never be shown as zero, and the difference is that this counts rows rather
     * than reads a value.
     */
    samplesByHour: z.array(z.number().int()),
    seriesHours: z.number().int(),
    tenants: z.array(z.object({
        tenantId: z.string().uuid(),
        tenantName: z.string(),
        samplesLast24h: z.number().int(),
        vehiclesReporting: z.number().int(),
    })),
});
/**
 * Partition headroom — the figure that decides whether ingestion has a future.
 *
 * `newestPartitionDate` is the START of the last partition, which covers that
 * whole day. `headroomDays` is therefore days of cover BEYOND today: zero means
 * today still works and tomorrow does not.
 */
export const pipelinePartitionsSchema = z.object({
    status: pipelineStatusSchema,
    partitionCount: z.number().int(),
    oldestPartitionDate: z.string().nullable(),
    newestPartitionDate: z.string().nullable(),
    headroomDays: z.number().int().nullable(),
    configuredLookaheadDays: z.number().int(),
    /** Days the oldest partition sits beyond the retention window, if the drop sweep is behind. */
    retentionDays: z.number().int(),
    daysBeyondRetention: z.number().int().nullable(),
    criticalBelowDays: z.number().int(),
    warningBelowDays: z.number().int(),
});
/**
 * Rollup lag.
 *
 * Kept separate from ingestion because they fail independently and confusing
 * them sends you to the wrong layer: raw can be arriving perfectly while every
 * trend, CAN health card and cohort comparison — all of which read the hourly
 * rollup, never raw — quietly goes stale.
 */
export const pipelineRollupSchema = z.object({
    status: pipelineStatusSchema,
    hoursAwaiting: z.number().int(),
    /** True when `hoursAwaiting` hit the query cap and is a floor, not a count. */
    backlogTruncated: z.boolean(),
    oldestAwaitingHour: z.string().datetime().nullable(),
    newestSummarisedHour: z.string().datetime().nullable(),
    newestSummarisedAgeHours: z.number().nullable(),
    /**
     * Awaiting hours whose raw samples are already past retention.
     *
     * These are unrecoverable — the rollup is driven by missing hours precisely
     * so a late worker can catch up, but it cannot summarise rows that have been
     * dropped. Any value above zero is data permanently lost, so it is CRITICAL
     * regardless of the backlog size.
     */
    hoursBeyondRecovery: z.number().int(),
    rawRetentionDays: z.number().int(),
    warningAboveHours: z.number().int(),
});
/**
 * Queue and event-processing backlog.
 *
 * `EventProcessingRecord` counts are here rather than in ingestion because they
 * answer a different question: whether events that arrived are becoming
 * incidents. A telemetry-derived event that is enqueued, fails, and is never
 * retried leaves raw telemetry intact and correct while the intelligence layer
 * silently produces nothing — which is exactly the shape of the missing
 * `EventProcessingRecord` defect found on 28 July 2026.
 */
export const pipelineQueueSchema = z.object({
    name: z.string(),
    waiting: z.number().int(),
    active: z.number().int(),
    failed: z.number().int(),
    delayed: z.number().int(),
    /** Set when Redis could not be reached; the counts are then all zero and meaningless. */
    unavailable: z.string().nullable(),
});
export const pipelineProcessingSchema = z.object({
    status: pipelineStatusSchema,
    queues: z.array(pipelineQueueSchema),
    /** `EventProcessingRecord` grouped by status, across every tenant. */
    events: z.array(z.object({ status: z.string(), count: z.number().int() })),
    failedEvents: z.number().int(),
    /**
     * Why the failed events failed, most common first.
     *
     * The page used to say "inspect `EventProcessingRecord.lastError` for the
     * cause", which names a database column at somebody holding a browser. The
     * column was always there; nothing read it.
     *
     * Messages are grouped VERBATIM and never fingerprinted — two errors that
     * differ by an id are two rows here. Collapsing them would need a similarity
     * rule nobody can check, and over-grouping hides a second fault behind a
     * first. `reason` is null for a record that failed with nothing recorded.
     */
    failedEventReasons: z.array(z.object({ reason: z.string().nullable(), count: z.number().int() })),
    /**
     * How many distinct messages exist, so a truncated list says so. Without it
     * the top few reasons read as the whole story.
     */
    distinctFailedEventReasons: z.number().int(),
    pendingEvents: z.number().int(),
    /** When the oldest still-unprocessed record last changed status. */
    oldestPendingAt: z.string().datetime().nullable(),
    oldestPendingAgeMinutes: z.number().nullable(),
});
/**
 * The stage BEFORE `processing` — a provider payload becoming a canonical event.
 *
 * ⚠️ **THIS STAGE WAS INSTRUMENTED BY NOTHING until 20 September 2026, and the
 * failure it hides is total.** `EventProcessingRecord` is keyed on a
 * `fleetEventId` and written at canonical persistence, so a payload that never
 * BECOMES a canonical event never gets a row — which means an adapter that
 * refuses every payload, a disabled connection, or an ingestion queue that has
 * stopped moving altogether all leave `processing` reporting the events that got
 * through before the stall, and the page green. Measured on the development
 * database at the time: 27 refused and 1 failed inbound event, and both health
 * surfaces reported zero.
 *
 * ⚠️ **NOT called `ingestion`, and the name is load-bearing.** That section
 * already exists on this response and means TELEMETRY SAMPLES. Two sections
 * named for one word, counting different objects, is a figure somebody reads off
 * the wrong one.
 *
 * ⚠️ **NEVER SUMMED WITH `processing`.** "failed to map" and "failed to
 * correlate" are different faults whose fixes live in different places — ours
 * and the tenant's reference data — so one combined number tells a reader that
 * something is wrong and nothing about which half.
 */
export const pipelineInboundSchema = z.object({
    status: pipelineStatusSchema,
    /** `RawProviderEvent` grouped by `processingStatus`, across every tenant. */
    events: z.array(z.object({ status: z.string(), count: z.number().int() })),
    /**
     * Payloads OIP could not map at all — a bad envelope, an event type no
     * adapter recognises, a disabled connection. Ours to fix in every case.
     */
    failed: z.number().int(),
    /**
     * Why, most common first. Grouped VERBATIM and never fingerprinted, for the
     * reason `failedEventReasons` gives: two messages differing by an id are two
     * things to go and look at.
     */
    failedReasons: z.array(z.object({ reason: z.string().nullable(), count: z.number().int() })),
    distinctFailedReasons: z.number().int(),
    /**
     * Payloads DELIBERATELY refused — today always a vehicle the tenant does not
     * hold. Counted separately from `failed` because it is the tenant's to fix and
     * the platform's only interest is the volume.
     */
    refused: z.number().int(),
    /**
     * Payloads accepted and not yet mapped.
     *
     * ⚠️ **The most valuable figure here.** A stalled ingestion queue piles rows up
     * at `QUEUE_PENDING` and produces no canonical events, so every counter in
     * `processing` holds still and the platform looks idle rather than broken.
     */
    pending: z.number().int(),
    /** When the oldest unmapped payload arrived, so a stall is legible as one. */
    oldestPendingAt: z.string().datetime().nullable(),
    oldestPendingAgeMinutes: z.number().nullable(),
});
export const pipelineHealthResponseSchema = z.object({
    generatedAt: z.string().datetime(),
    /** The worst of the five judged sections, so a nav badge needs one field. */
    status: pipelineStatusSchema,
    ingestion: pipelineIngestionSchema,
    partitions: pipelinePartitionsSchema,
    rollup: pipelineRollupSchema,
    /** Payload → canonical event. Runs BEFORE `processing`; see the note above. */
    inbound: pipelineInboundSchema,
    processing: pipelineProcessingSchema,
    /**
     * Recorded job runs — the section that answers "are we about to break".
     *
     * The other three describe outcomes, which stay green for as long as the
     * failing job's headroom lasts. This one reports the job itself.
     */
    jobs: pipelineJobsSchema,
});
//# sourceMappingURL=pipeline-health.js.map